import React from 'react';
import Select from 'react-select';
import { DropdownIndicator } from '../../common';

export interface SelectValue {
	value: string;
	label: string;
}

const reactSelectStyles = {
	control: (provided: any, state: any) => ({
		...provided,
		fontSize: '16px',
		borderRadius: 0,
		borderTop: 0,
		borderRight: 0,
		borderLeft: 0,
		boxShadow: 'none',
		backgroundColor: state.isDisabled ? 'rgba(0, 0, 0, 0)' : '',
		minHeight: '32px',
		borderBottom: state.isDisabled
			? '1px dotted rgba(0, 0, 0, 0.42)'
			: '1px solid rgba(0, 0, 0, 0.42)',
		boxSizing: 'content-box',
		':hover': {
			borderBottom: '1px solid rgba(0, 0, 0, 0.87)',
		},
		':focus': {
			borderBottom: '1px solid #3f51b5',
		},
		':focus:hover': {
			borderBottom: '1px solid #3f51b5',
		},
	}),
	container: (provided: any) => ({
		...provided,
		minHeight: '32px',
	}),
	singleValue: (provided: any, state: any) => ({
		...provided,
		color: state.isDisabled ? 'rgba(0, 0, 0, 0.54)' : provided.color,
		whiteSpace: 'normal',
		position: 'relative',
		top: 'initial',
		transform: 'none',
	}),
	multiValue: (provided: any, state: any) => ({
		...provided,
		backgroundColor: state.isDisabled
			? 'rgba(0, 0, 0, 0)'
			: provided.backgroundColor,
	}),
	multiValueLabel: (provided: any, state: any) => ({
		...provided,
		color: state.isDisabled ? 'rgba(0, 0, 0, 0.54)' : provided.color,
	}),
	multiValueRemove: (provided: any, state: any) => ({
		...provided,
		display: state.isDisabled ? 'none' : provided.display,
	}),
	valueContainer: (provided: any, state: any) => ({
		...provided,
		padding: '0px',
		flexWrap: 'wrap',
	}),
	option: (provided: any, state: any) => ({
		...provided,
		backgroundColor: state.isSelected
			? 'rgba(0, 0, 0, 0.08)'
			: provided.backgroundColor,
		color: 'inherit',
		':hover': {
			backgroundColor: state.isSelected
				? 'rgba(0, 0, 0, 0.08)'
				: 'rgba(0, 0, 0, 0.04);',
		},
	}),
	indicatorsContainer: (provided: any, state: any) => ({
		...provided,
		height: '31px',
	}),
	dropdownIndicator: (provided: any, state: any) => ({
		...provided,
		padding: '2px',
	}),
};

interface Props {
	id?: string;
	className?: string;
	value: SelectValue | SelectValue[]; // SelectValue[] used for isMulti
	options: SelectValue[];
	onChange: (option: SelectValue) => void;
	isDisabled?: boolean;
	components?: any;
	includeEmptyValue?: boolean;
	error?: boolean;
	styles?: any;
	disableOpenMenuOnFocus?: boolean;
	isMulti?: boolean;
}

const ReactSelect = (props: Props) => {
	const {
		id,
		className,
		value,
		options,
		onChange,
		isDisabled,
		components,
		includeEmptyValue,
		error,
		styles,
		disableOpenMenuOnFocus,
		isMulti,
	} = props;
	const finalStyles = { ...reactSelectStyles, ...styles };

	const allOptions = includeEmptyValue
		? ([{ value: '', label: <>&nbsp;</> }, ...options] as SelectValue[])
		: options;

	if (error) {
		const original = finalStyles.control({}, {});

		finalStyles.control = (provided: any, state: any) => ({
			...provided,
			...original,
			borderBottom: state.isDisabled
				? '1px dotted rgba(0, 0, 0, 0.42)'
				: '1px solid red',
			':hover': {
				borderBottom: '2px solid red',
			},
			':focus': {
				borderBottom: '2px solid red',
			},
			':focus:hover': {
				borderBottom: '2px solid red',
			},
		});
	}

	return (
		<>
			<Select
				styles={finalStyles}
				id={id}
				className={className}
				classNamePrefix='react-select'
				value={value}
				options={allOptions}
				onChange={onChange}
				isDisabled={isDisabled}
				openMenuOnFocus={!disableOpenMenuOnFocus}
				components={{
					IndicatorSeparator: (): any => null,
					DropdownIndicator,
					...components,
				}}
				menuPosition='fixed'
				theme={(theme: any) => ({
					...theme,
					borderRadius: 0,
					colors: {
						...theme.colors,
						primary25: 'rgba(0, 0, 0, 0.04);',
						primary: 'rgba(0, 0, 0, 0.08)',
					},
				})}
				isMulti={isMulti}
				hideSelectedOptions={false}
			/>
		</>
	);
};

export default ReactSelect;
